/**
 * Notification-click routing — Sprint 11 WI-4.
 *
 * One pure decision for "where does a notification click go?" so the in-app
 * notification-center click and the service-worker push click stay in lockstep
 * (push deep-link parity). The service worker forwards the FCM `data` payload,
 * where the server has flattened `meta` as `meta_{key}` (see
 * functions/notification_handlers.py). This helper reads that shape and returns
 * a routing decision; App.js performs the side effects.
 *
 * Pure and side-effect free — no React, no navigation, no Firestore.
 */
import { MY_ARENA_COMPLETED_NAV, MY_ARENA_SENT_NAV } from './lifecycleTransitionNotify';

export const NotificationClickAction = Object.freeze({
  PRIVATE_CHAT: 'private_chat',
  OPEN_FRIENDS: 'open_friends',
  BOOTSTRAP_NAV: 'bootstrap_nav',
  PENDING_INBOX: 'pending_inbox',
  NONE: 'none',
});

/** Arena bootstrap nav targets a notification may deep-link into. */
const ARENA_NAV_TARGETS = new Set([MY_ARENA_COMPLETED_NAV, MY_ARENA_SENT_NAV]);

/** @returns {boolean} whether a navigate target is a known My Arena tab. */
export function isArenaNavTarget(target) {
  return ARENA_NAV_TARGETS.has(target);
}

/**
 * Resolve a service-worker push notification click into a routing decision.
 *
 * Mirrors the in-app notification-center routing: messages open the private
 * chat, friend requests open Friends, and challenge/system notifications deep-
 * link to the My Arena tab named by `meta_navigate` (Completed/Sent) — falling
 * back to the pending inbox when no nav target is present.
 *
 * @param {object} [data] — FCM data payload (server-flattened, `meta_*` keys)
 * @returns {{ action: string, friendId?: string, title?: string, target?: string }}
 */
export function resolvePushNotificationClick(data = {}) {
  const type = data?.type;
  const friendId = data?.meta_friendId || data?.friendId;

  if (type === 'message' && friendId) {
    return {
      action: NotificationClickAction.PRIVATE_CHAT,
      friendId,
      title: data?.title || 'Friend',
    };
  }
  if (type === 'friend_request') {
    return { action: NotificationClickAction.OPEN_FRIENDS };
  }
  if (type === 'challenge' || type === 'system') {
    const target = data?.meta_navigate;
    if (isArenaNavTarget(target)) {
      return { action: NotificationClickAction.BOOTSTRAP_NAV, target };
    }
    return { action: NotificationClickAction.PENDING_INBOX };
  }
  return { action: NotificationClickAction.NONE };
}
